import {
  autoUpdate,
  useDismiss,
  useFloating,
  useInteractions,
  useTransitionStyles,
} from "@floating-ui/react"
import { useEffect, useMemo } from "react"

/**
 * Custom hook for creating and managing floating elements relative to a reference position
 *
 * @param show - Boolean controlling visibility of the floating element
 * @param referencePos - DOMRect, function returning DOMRect, or null representing the position to anchor the floating element to
 * @param zIndex - Z-index value for the floating element
 * @param options - Additional options to pass to the underlying useFloating hook
 * @returns Object containing properties and methods to control the floating element
 */
export function useFloatingElement(show, reference, zIndex, options, autoUpdateOptions) {
  const { dismissOptions, ...floatingOptions } = options || {}

  const { refs, context, floatingStyles, update } = useFloating({
    open: show,
    whileElementsMounted(referenceEl, floatingEl, updatePosition) {
      const cleanup = autoUpdate(referenceEl, floatingEl, updatePosition, autoUpdateOptions)
      return cleanup
    },
    ...floatingOptions,
  })

  const { isMounted, styles } = useTransitionStyles(context)

  const dismiss = useDismiss(context, dismissOptions)

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  useEffect(() => {
    if (reference === null) {
      refs.setReference(null)
      return
    }

    // If reference is an actual DOM element, use it directly
    // autoUpdate will automatically observe it for scroll/resize
    if (reference instanceof HTMLElement) {
      refs.setReference(reference)

      return
    }

    const getBoundingClientRect = () => {
      const rect = typeof reference === "function" ? reference() : reference
      return rect || new DOMRect();
    }

    refs.setReference({
      getBoundingClientRect,
    })
  }, [reference, refs])

  return useMemo(() => ({
    isMounted,
    ref: refs.setFloating,
    style: {
      ...styles,
      ...floatingStyles,
      zIndex,
    },
    getFloatingProps,
    getReferenceProps,
    update,
  }), [
    floatingStyles,
    isMounted,
    refs.setFloating,
    styles,
    zIndex,
    getFloatingProps,
    getReferenceProps,
    update,
  ]);
}
